import type { PointerEvent as ReactPointerEvent, Ref } from 'react';
import type { DraggableAttributes } from '@dnd-kit/core';
import {
	CheckIcon,
	EyeIcon,
	PencilIcon,
	SparklesIcon,
	TagIcon,
	TrashIcon,
} from '@heroicons/react/24/outline';
import type { Service } from '../../stores/servicesStore';

export interface ServiceCardProps {
	service: Service;
	canManage: boolean;
	onView: (service: Service) => void;
	onEdit?: (service: Service) => void;
	onDelete?: (service: Service) => void;

	/**
	 * Selection is opt-in: without `onToggleSelect` the card renders exactly as
	 * it always has, which is what the staff portal wants.
	 */
	onToggleSelect?: (id: string, extendRange: boolean) => void;
	isSelected?: boolean;
	/** Any selection exists — keeps every checkbox visible, not just on hover. */
	selectionActive?: boolean;

	/** Drag wiring from `useDraggable`; absent when the card isn't draggable. */
	dragRef?: Ref<HTMLDivElement>;
	dragListeners?: Record<string, unknown>;
	dragAttributes?: DraggableAttributes;
	/** Dimmed in place while the drag token follows the pointer. */
	isDragging?: boolean;
}

/**
 * One service in the grid. Presentational — every behaviour it has is passed
 * in, so the same component serves the plain staff list, the provider's
 * selectable/draggable list, and the drag overlay.
 */
const ServiceCard = ({
	service,
	canManage,
	onView,
	onEdit,
	onDelete,
	onToggleSelect,
	isSelected = false,
	selectionActive = false,
	dragRef,
	dragListeners,
	dragAttributes,
	isDragging = false,
}: ServiceCardProps) => {
	const classNames = [
		'service-card',
		isSelected ? 'service-card--selected' : '',
		isDragging ? 'service-card--dragging' : '',
		onToggleSelect ? 'service-card--selectable' : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className={classNames}>
			{/* The body is the drag handle — the footer keeps its buttons clickable. */}
			<div
				className="service-card__body"
				ref={dragRef}
				{...dragListeners}
				{...dragAttributes}
			>
				{onToggleSelect && (
					<button
						type="button"
						role="checkbox"
						aria-checked={isSelected}
						aria-label={`Select ${service.name}`}
						title="Select · Shift-click to select a range"
						className={`service-card__check ${
							isSelected || selectionActive ? 'service-card__check--visible' : ''
						}`}
						// Keeps the press off the drag sensors on the body — mouse
						// and touch listen to different events, so all three are
						// stopped and a press on the checkbox is only ever a click.
						onPointerDown={(e: ReactPointerEvent) => e.stopPropagation()}
						onMouseDown={e => e.stopPropagation()}
						onTouchStart={e => e.stopPropagation()}
						onClick={e => onToggleSelect(service.id, e.shiftKey)}
					>
						<CheckIcon className="w-3.5 h-3.5" />
					</button>
				)}

				<h3 className="service-card__name">{service.name}</h3>
				{service.description && (
					<p className="service-card__description">{service.description}</p>
				)}

				<div className="service-card__tags">
					<span className="service-card__meta">
						{service.variants.length} combination
						{service.variants.length !== 1 ? 's' : ''}
					</span>
					{service.addOn && (
						<span
							className="service-card__addon"
							title="Offered as an optional extra on your paid booking link"
						>
							<SparklesIcon className="w-3 h-3 flex-shrink-0" />
							Add-on
						</span>
					)}
					{service.category && (
						<span
							className={`service-card__category ${
								service.categoryId == null ? 'service-card__category--unfiled' : ''
							}`}
							title={
								service.categoryId == null
									? `"${service.category}" is a leftover label — this service isn't filed under a category yet`
									: `Category: ${service.category}`
							}
						>
							<TagIcon className="w-3 h-3 flex-shrink-0" />
							<span className="truncate">{service.category}</span>
						</span>
					)}
				</div>
			</div>

			<div className="service-card__footer">
				<button
					onClick={() => onView(service)}
					className={`service-card__action ${
						canManage ? 'service-card__action--view' : 'service-card__action--view-only'
					}`}
					title="View service"
				>
					<EyeIcon className="w-4 h-4" />
					{!canManage && <span>View Details</span>}
				</button>
				{canManage && onEdit && (
					<button
						onClick={() => onEdit(service)}
						className="service-card__action service-card__action--edit"
						title="Edit service"
					>
						<PencilIcon className="w-4 h-4" />
						<span>Edit</span>
					</button>
				)}
				{canManage && onDelete && (
					<button
						onClick={() => onDelete(service)}
						className="service-card__action service-card__action--delete"
						title="Delete service"
					>
						<TrashIcon className="w-4 h-4" />
					</button>
				)}
			</div>
		</div>
	);
};

export default ServiceCard;
